import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

export interface Share {
  id: string;
  userId: string;
  postId: string;
  platform: string;
  url?: string;
  createdAt: string;
  user?: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  post?: {
    id: string;
    title: string;
    content: string;
    creator: {
      id: string;
      username: string;
      firstName?: string;
      lastName?: string;
    };
  };
}

export interface ShareStats {
  totalShares: number;
  sharesByPlatform: {
    platform: string;
    count: number;
  }[];
  recentShares: Share[];
}

export interface ShareHistoryResponse {
  shares: Share[];
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalCount: number;
  };
}

export interface TopSharedPost {
  id: string;
  title: string;
  content: string;
  shareCount: number;
  creator: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  createdAt: string;
}

/**
 * Track a share action
 */
export const trackShare = async (
  postId: string,
  platform: string,
  url: string
): Promise<Share> => {
  const token = localStorage.getItem('token');
  const response = await axios.post(
    `${API_URL}/api/shares`,
    { postId, platform, url },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return response.data.share;
};

/**
 * Get share statistics for a post
 */
export const getShareStats = async (postId: string): Promise<ShareStats> => {
  const response = await axios.get(`${API_URL}/api/shares/stats/${postId}`);
  return response.data;
};

/**
 * Get user's share history
 */
export const getShareHistory = async (
  page = 1,
  limit = 20
): Promise<ShareHistoryResponse> => {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_URL}/api/shares/history`, {
    params: { page, limit },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

/**
 * Get top shared posts
 */
export const getTopSharedPosts = async (
  limit = 10,
  timeframe: '7d' | '30d' | 'all' = '7d'
): Promise<{ posts: TopSharedPost[]; timeframe: string }> => {
  const response = await axios.get(`${API_URL}/api/shares/top`, {
    params: { limit, timeframe },
  });
  return response.data;
};

export default {
  trackShare,
  getShareStats,
  getShareHistory,
  getTopSharedPosts,
};
